/**
 * The dashboard's Users tab: the newest sign-ups, from the same endpoint as the Users page,
 * sorted by join date. Only columns the admin user DTO actually carries; everything else about a
 * user is one tap away on their detail page.
 */

import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Avatar, Box, Button, Chip, Stack, Typography } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import type { AdminUserDto } from '@api/types/admin'
import { useRecentUsers } from '../../hooks/useDashboardRecent'
import { errorMessage, formatDate } from '../users/userFormatting'

export function RecentUsersTable() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const { data, isLoading, error, refetch } = useRecentUsers(page, pageSize, search)

  const columns = useMemo<DataTableColumn<AdminUserDto>[]>(
    () => [
      {
        key: 'user',
        header: 'User',
        primary: true,
        render: (user) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar src={user.profilePictureUrl || user.avatar || undefined} sx={{ width: 36, height: 36 }}>
              {(user.displayName || user.email || '?')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
                {user.displayName || user.userName || 'No display name'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        key: 'joined',
        header: 'Joined',
        render: (user) => <Typography variant="caption">{formatDate(user.createdAt)}</Typography>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (user) => (
          <Chip
            label={user.isActive ? 'Active' : 'Deactivated'}
            color={user.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        hideBelow: 'md',
        render: (user) =>
          user.roles.length > 0 ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {user.roles.map((role) => (
                <Chip key={role} label={role} size="small" variant="outlined" />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              None
            </Typography>
          ),
      },
      {
        key: 'lastLogin',
        header: 'Last login',
        hideBelow: 'md',
        render: (user) => <Typography variant="caption">{formatDate(user.lastLoginAt)}</Typography>,
      },
    ],
    []
  )

  return (
    <DataTable
      rows={data?.items ?? []}
      columns={columns}
      getRowKey={(user) => user.id}
      loading={isLoading}
      error={error ? errorMessage(error, 'Could not load recent users') : null}
      onRetry={() => refetch()}
      emptyMessage={search ? 'No users match that search' : 'No users yet'}
      page={page}
      pageSize={pageSize}
      total={data?.totalCount ?? 0}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size)
        setPage(0)
      }}
      pageSizeOptions={[5, 10, 25]}
      onRowClick={(user) => navigate(`/users/${user.id}`)}
      getRowActions={(user) => [
        {
          key: 'open',
          label: 'Open',
          icon: <OpenInNew />,
          hint: 'Open this user',
          onClick: () => navigate(`/users/${user.id}`),
        },
      ]}
      toolbar={
        <>
          <SearchField
            value={search}
            onSearch={(q) => {
              setSearch(q)
              setPage(0)
            }}
            placeholder="Search name or email"
          />
          <Button component={RouterLink} to="/users" size="small">
            All users
          </Button>
        </>
      }
    />
  )
}
